import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ACCESS_TOKEN, IMPERSONATION } from "./constants/commonConst";
import { PLATFORM_STATS } from "./constants/landing";

import Menu from "./components/menu/Menu";
import Login from "./components/login/Login";
import Signup from "./components/signup/Signup";
import Activate from "./components/activate/Activate";
import GoogleSignInComplete from "./components/googleSignIn/GoogleSignInComplete";
import ForgotPassword from "./components/password/ForgotPassword";
import ResetPassword from "./components/password/ResetPassword";
import AcceptInvite from "./components/acceptInvite/AcceptInvite";
import AcceptAdminInvite from "./components/acceptAdminInvite/AcceptAdminInvite";
import ManageBooking from "./components/manageBooking/ManageBooking";
import Terms from "./components/legal/Terms";
import Privacy from "./components/legal/Privacy";
import Cookies from "./components/legal/Cookies";
import Delivery from "./components/legal/Delivery";
import Cancellation from "./components/legal/Cancellation";
import Withdrawal from "./components/legal/Withdrawal";
import About from "./components/about/About";
import Counts from "./components/counts/Counts";
import Services from "./components/services/Services";
import Portfolio from "./components/portfolio/Portfolio";
import PortfolioDetails from "./components/portfolio/PortfolioDetails";
import Pricing from "./components/pricing/Pricing";
import Contact from "./components/contact/Contact";
import Discover from "./components/discover/Discover";
import Footer from "./components/footer/Footer";
import ScrollToTop from "./components/ui/ScrollToTop";
import SnackbarHost from "./components/ui/SnackbarHost";
import CookieConsent from "./components/cookies/CookieConsent";
import Dashboard from "./components/dashboard/Dashboard";
import Organization from "./components/dashboard/pages/Organization";
import OrganizationWizard from "./components/dashboard/pages/wizard/OrganizationWizard";
import Reports from "./components/dashboard/pages/Reports";
import Bookings from "./components/dashboard/pages/Bookings";
import Calendar from "./components/dashboard/pages/Calendar";
import DashboardServices from "./components/dashboard/pages/DashboardServices";
import Resources from "./components/dashboard/pages/Resources";
import MySite from "./components/dashboard/pages/MySite";
import Settings from "./components/dashboard/pages/Settings";
import Billing from "./components/dashboard/pages/Billing";
import PaymentReturn from "./components/dashboard/pages/PaymentReturn";
import Support from "./components/dashboard/pages/Support";
import PlatformOverview from "./components/dashboard/pages/platform/PlatformOverview";
import Companies from "./components/dashboard/pages/platform/Companies";
import Invitations from "./components/dashboard/pages/platform/Invitations";
import Admins from "./components/dashboard/pages/platform/Admins";
import PlatformPricing from "./components/dashboard/pages/platform/PlatformPricing";
import BookAppointment from "./components/dashboard/pages/customer/BookAppointment";
import MyBookings from "./components/dashboard/pages/customer/MyBookings";
import CustomerOverview from "./components/dashboard/pages/customer/CustomerOverview";
import BookingPage from "./components/BookingPage";

import {
  logout,
  isAuthenticated,
  getUserInfoFromToken,
  keepSessionAlive,
  sessionNeedsRenewal,
} from "./store/action";
import {
  canAccessPath,
  getDefaultRouteForRole,
  isSetupGatedPath,
} from "./constants/navigation";
import { resolveOrganizationSetupComplete } from "./utils/organizationSetup";
import { spinnerClass } from "./components/dashboard/dashboardStyles";
import { PageMeta } from "./hooks/usePageMeta";

import "./assets/css/App.css";
import "./assets/css/style.css";

// Bootstrap itself is imported (layered) from index.css so Tailwind utilities
// can win over it. These two are icon fonts only and don't conflict.
import "./assets/vendor/bootstrap-icons/bootstrap-icons.css";
import "./assets/vendor/boxicons/css/boxicons.min.css";

/**
 * What the route guards need from the token: the role, and whether this is a
 * SystemOwner simulating a business, which has a menu of its own.
 */
const currentNavContext = () => {
  const info = getUserInfoFromToken();
  return {
    role: info?.role,
    options: { impersonating: info?.impersonatorId != null },
  };
};

/**
 * A page no search result should lead to: a token link (dead without its
 * token, and every dead one reads the same — a pile of duplicates), or
 * anything behind a sign-in. Every other public route names its canonical URL
 * with a <PageMeta path> instead; see hooks/usePageMeta.js. A new route with a
 * <PageMeta path> also goes into the sitemap's list, `StaticPages` in the API's
 * PublicSitemap.cs, or the sitemap leaves it out.
 */
const unlisted = (element) => (
  <>
    <PageMeta noindex />
    {element}
  </>
);

/** Where the signed-in user belongs — their role's first tab. */
const homeForCurrentUser = () => {
  const { role, options } = currentNavContext();
  return getDefaultRouteForRole(role, options);
};

/**
 * A dashboard page: authenticated, permitted for the role, wrapped in the
 * dashboard chrome.
 *
 * The role check lives here rather than in each page so that a tab hidden from
 * the sidebar is also unreachable by typing its URL — an employee who guesses
 * /dashboard/reports gets bounced to their own home, not an admin's page. This
 * is UX, not security: the API independently re-checks every request, since
 * the token this reads is the client's to tamper with.
 *
 * The same reasoning extends to the 8 setup-gated items (see
 * `isSetupGatedPath`): DashboardMenu.jsx only disables their tab, so a gated
 * role that types the URL directly is bounced back to the Organization page
 * (the wizard's home) instead of rendering the page underneath.
 */
const DashboardRoute = ({ children, authenticated, onLogout }) => {
  const location = useLocation();
  // Read unconditionally (hooks can't follow the `authenticated` early return
  // below); harmless when there's no session since every check downstream
  // treats a missing role as "not gated".
  const { role, options: navOptions } = authenticated
    ? currentNavContext()
    : { role: undefined, options: undefined };
  const pathIsGated =
    authenticated && isSetupGatedPath(role, location.pathname, navOptions);

  // `checked` starts true for any path that isn't gated, so ungated pages
  // (Organization, Settings, Support, and everything for a non-admin role)
  // never wait on a fetch. Only a genuinely gated path pays for the check.
  const [setupState, setSetupState] = useState({
    checked: !pathIsGated,
    complete: true,
  });

  useEffect(() => {
    if (!pathIsGated) {
      setSetupState({ checked: true, complete: true });
      return;
    }

    let cancelled = false;
    setSetupState((prev) => ({ ...prev, checked: false }));
    resolveOrganizationSetupComplete(role).then((complete) => {
      if (!cancelled) setSetupState({ checked: true, complete });
    });

    return () => {
      cancelled = true;
    };
  }, [pathIsGated, role, location.pathname]);

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!canAccessPath(role, location.pathname, navOptions)) {
    return <Navigate to={getDefaultRouteForRole(role, navOptions)} replace />;
  }

  // Waiting on the one-time status check for a gated path — render a plain
  // spinner rather than flash the gated page before possibly redirecting.
  if (pathIsGated && !setupState.checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50">
        <div role="status" className={spinnerClass} />
      </div>
    );
  }

  if (pathIsGated && !setupState.complete) {
    return <Navigate to="/dashboard/organization" replace />;
  }

  return <Dashboard onLogout={onLogout}>{children}</Dashboard>;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute = ({ children, authenticated }) => {
  if (authenticated) {
    return <Navigate to={homeForCurrentUser()} replace />;
  }
  return children;
};

/**
 * `router` is for the build's prerender (scripts/prerender.mjs, through
 * entry-prerender.jsx), which has no address bar and hands in a StaticRouter
 * set to the page it is writing. The browser always gets BrowserRouter.
 */
function App({ router: Router = BrowserRouter }) {
  // Read the token during the first render, not in an effect. Starting at
  // `false` made every ProtectedRoute redirect to /login on that first pass,
  // and PublicRoute then bounced the (actually signed-in) user on to
  // /dashboard/organization — so refreshing or deep-linking any dashboard tab
  // silently landed on the wrong page. localStorage is synchronous, so there is
  // nothing to wait for.
  const [authenticated, setAuthenticated] = useState(isAuthenticated);

  // The one thing that is worth waiting for: a remembered browser coming back
  // after its token ran out (any break longer than a quarter of an hour). It
  // renews before anything renders, so the dashboard opens as it was left — or,
  // when the week has passed, the login page opens instead of the dashboard
  // flashing up first and then giving way to it.
  const [renewing, setRenewing] = useState(sessionNeedsRenewal);

  useEffect(() => {
    if (!renewing) return undefined;

    let cancelled = false;
    keepSessionAlive().then((alive) => {
      if (cancelled) return;
      if (!alive) handleLogout();
      setRenewing(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    checkAuthStatus();

    // Every minute: renew a remembered sign-in shortly before its token runs
    // out, and end any session that is over.
    const tokenCheckInterval = setInterval(async () => {
      if (authenticated && !(await keepSessionAlive())) {
        console.warn("Session ended, logging out...");
        handleLogout();
      }
    }, 60000);

    return () => clearInterval(tokenCheckInterval);
  }, [authenticated]);

  const checkAuthStatus = () => {
    // Use the isAuthenticated function which checks token expiration
    const isAuth = isAuthenticated();
    setAuthenticated(isAuth);

    // If token exists but is expired, clean up
    const token = localStorage.getItem(ACCESS_TOKEN);
    if (token && !isAuth) {
      localStorage.removeItem(ACCESS_TOKEN);
      localStorage.removeItem("USER_EMAIL");
      localStorage.removeItem(IMPERSONATION);
    }
  };

  const handleLogout = () => {
    // Use the centralized logout function
    logout(false); // Don't redirect, we'll handle state update
    setAuthenticated(false);
  };

  const handleLoginSuccess = () => {
    setAuthenticated(true);
  };

  if (renewing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50">
        <div role="status" className={spinnerClass} />
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      {/* Every save in the app reports here (utils/notify.js). Outside the
          routes, so the message survives the navigation a save often causes. */}
      <SnackbarHost />
      {/* The cookie banner and the Meta Pixel it governs. Before the routes,
          so the banner is the first thing a keyboard reaches. */}
      <CookieConsent />
      <div className="App">
        <Routes>
          {/* Public Routes with Menu and Footer */}
          <Route
            path="/"
            element={
              <PublicRoute authenticated={authenticated}>
                <>
                  <PageMeta path="/" />
                  <Menu authenticated={authenticated} onLogout={handleLogout} />
                  <About />
                  {PLATFORM_STATS.enabled && <Counts />}
                  <Services />
                  <Portfolio />
                  <Pricing />
                  <Contact />
                  <Footer />
                </>
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute authenticated={authenticated}>
                <>
                  <PageMeta path="/login" />
                  <Menu authenticated={authenticated} onLogout={handleLogout} />
                  <Login
                    authenticated={authenticated}
                    onLoginSuccess={handleLoginSuccess}
                  />
                  <Footer />
                </>
              </PublicRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicRoute authenticated={authenticated}>
                <>
                  <PageMeta path="/signup" />
                  <Menu authenticated={authenticated} onLogout={handleLogout} />
                  <Signup authenticated={authenticated} />
                  <Footer />
                </>
              </PublicRoute>
            }
          />
          {/* Activation lands here from the emailed link. Deliberately NOT
              wrapped in PublicRoute: someone still holding a valid session from
              another account would be bounced to the dashboard and the link
              would go unredeemed. Must stay above /:organizationSlug, which
              would otherwise swallow "/activate" as a business slug. */}
          <Route path="/activate" element={unlisted(<Activate />)} />

          {/* The end of "Continue with Google": the API's callback lands here
              with a one-minute code in the fragment. Same placement rule as
              /activate, and outside PublicRoute for the same reason — a session
              already in this browser must not bounce the sign-in away. */}
          <Route
            path="/auth/google"
            element={unlisted(<GoogleSignInComplete onLoginSuccess={handleLoginSuccess} />)}
          />

          {/* Password recovery. Same placement rule as /activate: above
              /:organizationSlug, and outside PublicRoute so a stale session
              cannot bounce someone away from their own reset link. */}
          <Route path="/forgot-password" element={unlisted(<ForgotPassword />)} />
          <Route path="/reset-password" element={unlisted(<ResetPassword />)} />

          {/* Resource invites land here from the emailed link. Same placement
              rule as /activate and /reset-password: above /:organizationSlug
              (which would otherwise swallow "/accept-invite" as a business
              slug), and outside PublicRoute — a stale session from another
              account must not bounce the invitee away before the link is
              redeemed. */}
          <Route path="/accept-invite" element={unlisted(<AcceptInvite />)} />

          {/* Administrator invites, same placement rule and same reasoning as
              /accept-invite above. Its own route rather than a mode of that
              one: this link creates platform staff, who have no organization,
              and the two flows have nothing in common past the shape of the
              page. */}
          <Route path="/accept-admin-invite" element={unlisted(<AcceptAdminInvite />)} />

          {/* The manage link from a booking email. Same placement rule as
              /activate: above /:organizationSlug, which would otherwise swallow
              "/booking" as a business slug. Outside PublicRoute for a stronger
              reason than the others — the people this page exists for booked as
              guests and have no account at all, so bouncing a signed-in visitor
              to a dashboard would strand exactly the customer it serves (a
              salon owner opening their own customer's link, or anyone on a
              shared machine). */}
          <Route path="/booking/:token" element={unlisted(<ManageBooking />)} />

          {/* Legal documents. Same placement rule as /activate: above
              /:organizationSlug, which would otherwise swallow "/terms" as a
              business slug. Deliberately NOT wrapped in PublicRoute — the terms
              a signed-in user agreed to must stay readable while signed in, and
              the signup checkbox links here from an unauthenticated page. */}
          {[
            ["/terms", <Terms />],
            ["/privacy", <Privacy />],
            ["/cookies", <Cookies />],
            ["/delivery", <Delivery />],
            ["/cancellation", <Cancellation />],
            // The withdrawal facility required by art. 11^1 of OUG 34/2014.
            // It belongs in this block for the same reason as the rest, and
            // for one of its own: it has to work for somebody with no account
            // left, so it must never sit behind a route guard.
            ["/withdrawal", <Withdrawal />],
          ].map(([path, element]) => (
            <Route
              key={path}
              path={path}
              element={
                <>
                  <PageMeta path={path} />
                  <Menu authenticated={authenticated} onLogout={handleLogout} />
                  {element}
                  <Footer />
                </>
              }
            />
          ))}

          {/* Public business search. Same placement rule as /activate: above
              /:organizationSlug, which would otherwise swallow "/discover" as a
              business slug. Deliberately NOT wrapped in PublicRoute — a signed-in
              owner looking up another business is a normal thing to do, and
              bouncing them to their dashboard would make the menu link dead for
              exactly the people most likely to click it. */}
          <Route
            path="/discover"
            element={
              <>
                <PageMeta path="/discover" />
                <Menu authenticated={authenticated} onLogout={handleLogout} />
                <Discover />
                <Footer />
              </>
            }
          />

          {/* Unlisted: nothing hands it any items, so every id renders the
              same "not found". */}
          <Route
            path="/portfolio/:id"
            element={unlisted(
              <>
                <Menu authenticated={authenticated} onLogout={handleLogout} />
                <PortfolioDetails />
                <Footer />
              </>,
            )}
          />

          {/* Protected Dashboard Routes. Every path here must also appear in
              constants/navigation.js, or no role will be able to reach it. */}
          {[
            ["/dashboard/organization", <Organization />],
            ["/dashboard/reports", <Reports />],
            ["/dashboard/bookings", <Bookings />],
            ["/dashboard/calendar", <Calendar />],
            ["/dashboard/services", <DashboardServices />],
            ["/dashboard/resources", <Resources />],
            ["/dashboard/site", <MySite />],
            ["/dashboard/settings", <Settings />],
            ["/dashboard/billing", <Billing />],
            // Nested under billing on purpose: canAccessPath matches by prefix,
            // so this inherits the billing tab's permissions without becoming a
            // sidebar item of its own. It is where NETOPIA returns the customer
            // after the card form.
            ["/dashboard/billing/payment-return", <PaymentReturn />],
            // Billing's other two tabs, nested the same way and for the same
            // reason: they are part of billing, and the billing item's
            // permission is the right one. One page, told which tab to show.
            ["/dashboard/billing/sms", <Billing tab="sms" />],
            ["/dashboard/billing/payments", <Billing tab="payments" />],
            // The address this used to live at, kept resolving on purpose: a
            // payment opened before billing got its own tab was handed the old
            // URL and will come back to it, and NETOPIA decides when.
            ["/dashboard/settings/payment-return", <PaymentReturn />],
            ["/dashboard/support", <Support />],
            ["/dashboard/platform", <PlatformOverview />],
            ["/dashboard/companies", <Companies />],
            ["/dashboard/invitations", <Invitations />],
            ["/dashboard/admins", <Admins />],
            ["/dashboard/pricing", <PlatformPricing />],
            ["/dashboard/overview", <CustomerOverview />],
            ["/dashboard/book", <BookAppointment />],
            ["/dashboard/my-bookings", <MyBookings />],
          ].map(([path, element]) => (
            <Route
              key={path}
              path={path}
              element={unlisted(
                <DashboardRoute
                  authenticated={authenticated}
                  onLogout={handleLogout}
                >
                  {element}
                </DashboardRoute>,
              )}
            />
          ))}

          {/* The setup wizard, one address per step. Nested under the
              Organization tab so canAccessPath's prefix match gives it that
              tab's permissions (and keeps that tab lit in the sidebar); the
              step is optional so the bare address resumes wherever the
              business had got to. It sits in the dashboard chrome like every
              other page: on a route of its own, without the sidebar, it read
              as a different site. */}
          <Route
            path="/dashboard/organization/setup/:step?"
            element={unlisted(
              <DashboardRoute
                authenticated={authenticated}
                onLogout={handleLogout}
              >
                <OrganizationWizard />
              </DashboardRoute>,
            )}
          />

          {/* Public Booking Page Route - Must be before catch-all. Names its
              own canonical once the API has said which slug is the real one. */}
          <Route path="/:organizationSlug" element={<BookingPage />} />

          {/* Catch all - redirect to home or dashboard based on auth */}
          <Route
            path="*"
            element={
              authenticated ? (
                <Navigate to={homeForCurrentUser()} replace />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
